import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "./Navbar";
import API_CONFIG, { buildApiUrl } from '../config/api';

const CreateEvent = () => {
    const [eventTypes, setEventTypes] = useState([]);
    const [events, setEvents] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [selectedEventType, setSelectedEventType] = useState('');
    const [eventName, setEventName] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [groups, setGroups] = useState("");
    const [editingEvent, setEditingEvent] = useState(null);
    const [editFormData, setEditFormData] = useState({
        event_name: '',
        event_type_id: '',
        event_date: '',
        active: true
    });
    const [filters, setFilters] = useState({
        eventType: '',
        startDate: '',
        endDate: '',
        showActive: 'active' // Changed from 'all' to 'active'
    });

    useEffect(() => {
        fetchEventTypes();
        fetchEvents();
    }, []);

    const fetchEventTypes = async () => {
        try {
            const token = localStorage.getItem("token"); // Retrieve token
            if (!token) {
                console.error("No authentication token found.");
                return;
            } else {
                const decoded = JSON.parse(atob(token.split(".")[1]));
                setGroups(decoded["cognito:groups"]);
            }
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.EVENT_TYPES), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Failed to fetch event types');
            const data = await response.json();
            setEventTypes(data);
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchEvents = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Failed to fetch events');
            const data = await response.json();
            setEvents(data);
            setFilteredEvents(data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleEventTypeChange = (e) => {
        setSelectedEventType(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const eventData = {
                event_type_id: parseInt(selectedEventType),
                event_name: eventName,
                event_date: eventDate,
                active: true
            };

            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(eventData)
            });

            if (!response.ok) throw new Error('Failed to create event');
            
            // Refresh events list
            fetchEvents();
            
            // Reset form
            setEventName('');
            setEventDate('');
            setSelectedEventType('');
        } catch (err) {
            setError(err.message);
        }
    };

    const handleEdit = (event) => {
        setEditingEvent(event.event_id);
        setEditFormData({
            event_name: event.event_name,
            event_type_id: event.event_type_id,
            event_date: event.event_date.split('T')[0],
            active: event.active
        });
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSaveEdit = async (eventId) => {
        try {
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    ...editFormData,
                    event_id: eventId
                })
            });

            if (!response.ok) throw new Error('Failed to update event');
            
            // Refresh events list
            fetchEvents();
            setEditingEvent(null);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleCancelEdit = () => {
        setEditingEvent(null);
        setEditFormData({
            event_name: '',
            event_type_id: '',
            event_date: '',
            active: true
        });
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyFilters = () => {
        let filtered = [...events];

        // Filter by event type
        if (filters.eventType) {
            filtered = filtered.filter(event => 
                event.event_type_id === parseInt(filters.eventType)
            );
        }

        // Filter by date range
        if (filters.startDate) {
            filtered = filtered.filter(event => 
                new Date(event.event_date) >= new Date(filters.startDate)
            );
        }
        if (filters.endDate) {
            filtered = filtered.filter(event => 
                new Date(event.event_date) <= new Date(filters.endDate)
            );
        }

        // Filter by active status
        if (filters.showActive !== 'all') {
            const isActive = filters.showActive === 'active';
            filtered = filtered.filter(event => event.active === isActive);
        }

        setFilteredEvents(filtered);
    };

    useEffect(() => {
        applyFilters();
    }, [filters, events]);

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <div className="container mt-4">
            <Navbar groups={groups} />
            <h2>Create Event</h2>
            <form onSubmit={handleSubmit}>
                <div className="mb-3">
                    <label className="form-label">Event Type</label>
                    <select 
                        className="form-select" 
                        value={selectedEventType} 
                        onChange={handleEventTypeChange}
                        required
                    >
                        <option value="">Select Event Type</option>
                        {eventTypes.map(type => (
                            <option key={type.event_type_id} value={type.event_type_id}>
                                {type.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="mb-3">
                    <label className="form-label">Event Name</label>
                    <input 
                        type="text" 
                        className="form-control" 
                        value={eventName} 
                        onChange={(e) => setEventName(e.target.value)}
                        required
                    />
                </div>

                <div className="mb-3">
                    <label className="form-label">Event Date</label>
                    <input 
                        type="date" 
                        className="form-control" 
                        value={eventDate} 
                        onChange={(e) => setEventDate(e.target.value)}
                        required
                    />
                </div>

                <button type="submit" className="btn btn-primary">Create Event</button>
            </form>

            <h3 className="mt-4">Existing Events</h3>
            
            {/* Filter Pane */}
            <div className="card mb-4">
                <div className="card-body">
                    <h5 className="card-title">Filter Events</h5>
                    <div className="row">
                        <div className="col-md-3 mb-3">
                            <label className="form-label">Event Type</label>
                            <select
                                className="form-select"
                                name="eventType"
                                value={filters.eventType}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Types</option>
                                {eventTypes.map(type => (
                                    <option key={type.event_type_id} value={type.event_type_id}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3 mb-3">
                            <label className="form-label">Start Date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="startDate"
                                value={filters.startDate}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-3 mb-3">
                            <label className="form-label">End Date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="endDate"
                                value={filters.endDate}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-3 mb-3">
                            <label className="form-label">Status</label>
                            <select
                                className="form-select"
                                name="showActive"
                                value={filters.showActive}
                                onChange={handleFilterChange}
                            >
                                <option value="all">All Events</option>
                                <option value="active">Active Only</option>
                                <option value="inactive">Inactive Only</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div className="table-responsive">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Type</th>
                            <th>Date</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredEvents.map(event => (
                            <tr key={event.event_id}>
                                <td>
                                    {editingEvent === event.event_id ? (
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="event_name"
                                            value={editFormData.event_name}
                                            onChange={handleEditChange}
                                        />
                                    ) : (
                                        event.event_name
                                    )}
                                </td>
                                <td>
                                    {editingEvent === event.event_id ? (
                                        <select
                                            className="form-select"
                                            name="event_type_id"
                                            value={editFormData.event_type_id}
                                            onChange={handleEditChange}
                                        >
                                            {eventTypes.map(type => (
                                                <option key={type.event_type_id} value={type.event_type_id}>
                                                    {type.name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        event.event_type_name
                                    )}
                                </td>
                                <td>
                                    {editingEvent === event.event_id ? (
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="event_date"
                                            value={editFormData.event_date}
                                            onChange={handleEditChange}
                                        />
                                    ) : (
                                        new Date(event.event_date).toLocaleDateString()
                                    )}
                                </td>
                                <td>
                                    {editingEvent === event.event_id ? (
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                name="active"
                                                checked={editFormData.active}
                                                onChange={handleEditChange}
                                            />
                                        </div>
                                    ) : (
                                        event.active ? 'Active' : 'Inactive'
                                    )}
                                </td>
                                <td>
                                    {editingEvent === event.event_id ? (
                                        <>
                                            <button
                                                className="btn btn-success btn-sm me-2"
                                                onClick={() => handleSaveEdit(event.event_id)}
                                            >
                                                Save
                                            </button>
                                            <button
                                                className="btn btn-secondary btn-sm"
                                                onClick={handleCancelEdit}
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            className="btn btn-primary btn-sm"
                                            onClick={() => handleEdit(event)}
                                        >
                                            Edit
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CreateEvent; 